import { useEffect, useState, type ComponentProps } from "react";
import type { DeliverySummary, MeResponse, PostKind, PostSummary } from "../../api/client";
import { DeliveryCenter } from "../delivery/DeliveryCenter";
import { PostEditor, type PostEditorApi } from "./PostEditor";
import type { ButtonLayoutLimits } from "../composer/button-layout-model";
import { categorySelectionKeys, isWholePriceByCategory } from "../price/category-model";

export const isRecurringPost = (post: PostSummary) =>
  post.scheduleType === "interval" ||
  post.scheduleType === "daily" ||
  post.postOnChangeOnly === true ||
  post.editOnChangeOnly === true;

export const postListTitle = (post: PostSummary) => {
  if (post.kind !== "price" || post.categories === undefined) {
    return post.title || (post.kind === "price" ? "Прайс-пост" : "Свой пост");
  }
  if (isWholePriceByCategory(post.categories)) return "Весь прайс по категориям";
  const categories = categorySelectionKeys(post.categories)
    .map((category) => category.trim())
    .filter(Boolean);
  if (categories.length === 0) return "Весь прайс";
  const visible = categories.slice(0, 2).join(" · ");
  return categories.length > 2 ? `${visible} · ещё ${categories.length - 2}` : visible;
};

const scheduleLabel = (post: PostSummary) => {
  if (post.editOnChangeOnly) return "Авторедактируется при изменении прайса";
  if (post.postOnChangeOnly) return "Публикуется при изменении прайса";
  if (post.scheduleType === "daily") return `Ежедневно ${post.dailyTime ?? ""} мск`;
  if (post.scheduleType === "interval") return `Каждые ${post.intervalMinutes} мин.`;
  return "Однократная публикация";
};

export function PostsScreen({ loadPosts, capabilities, buttonLimits, deliveryApi, editApi, initialTab = "posts", onDeliveriesChange }: {
  loadPosts: (signal?: AbortSignal) => Promise<PostSummary[]>;
  capabilities: MeResponse["capabilities"];
  buttonLimits: ButtonLayoutLimits;
  deliveryApi: ComponentProps<typeof DeliveryCenter>["api"];
  editApi: PostEditorApi & { deletePost: (kind: PostKind, id: string) => Promise<void> };
  initialTab?: "posts" | "deliveries";
  onDeliveriesChange?: (items: DeliverySummary[]) => void;
}) {
  const [items, setItems] = useState<PostSummary[] | null>(null);
  const [tab, setTab] = useState<"posts" | "deliveries">(initialTab);
  const [postSegment, setPostSegment] = useState<"recurring" | "one-time">("recurring");
  const [selected, setSelected] = useState<{ kind: PostKind; id: string } | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [deleteCandidate, setDeleteCandidate] = useState<{ post: PostSummary; title: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    void loadPosts(controller.signal).then(setItems, () => {
      if (!controller.signal.aborted) setItems([]);
    });
    return () => controller.abort();
  }, [loadPosts, refreshKey]);

  if (selected) {
    return <PostEditor kind={selected.kind} postId={selected.id} capabilities={capabilities} buttonLimits={buttonLimits} api={editApi} onClose={() => {
      setSelected(null);
      setItems(null);
      setRefreshKey((value) => value + 1);
    }} />;
  }

  const recurring = items?.filter(isRecurringPost) ?? [];
  const oneTime = items?.filter((post) => !isRecurringPost(post)) ?? [];
  const visiblePosts = postSegment === "recurring" ? recurring : oneTime;
  const emptyTitle = postSegment === "recurring"
    ? "Регулярных публикаций пока нет"
    : "Однократных публикаций пока нет";

  const confirmDelete = async () => {
    if (!deleteCandidate) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await editApi.deletePost(deleteCandidate.post.kind, deleteCandidate.post.id);
      setItems((current) => current?.filter((post) => post.kind !== deleteCandidate.post.kind || post.id !== deleteCandidate.post.id) ?? current);
      setDeleteCandidate(null);
    } catch {
      setDeleteError("Не удалось удалить публикацию. Попробуйте ещё раз.");
    } finally {
      setDeleting(false);
    }
  };

  return <section className="screen-stack section-screen">
    <header><p className="eyebrow">Ваши публикации</p><h1>Посты</h1></header>
    <div className="segmented-control" role="tablist" aria-label="Посты и доставки"><button type="button" role="tab" aria-selected={tab === "posts"} onClick={() => setTab("posts")}>Публикации</button><button type="button" role="tab" aria-selected={tab === "deliveries"} onClick={() => setTab("deliveries")}>Доставки</button></div>
    {tab === "deliveries" ? <DeliveryCenter capabilities={capabilities} api={deliveryApi} onItemsChange={onDeliveriesChange} /> : <div className="post-library-segments">
      {items !== null && <div className="segmented-control post-type-segments" role="tablist" aria-label="Тип публикаций"><button type="button" role="tab" aria-selected={postSegment === "recurring"} onClick={() => setPostSegment("recurring")}>Регулярные ({recurring.length})</button><button type="button" role="tab" aria-selected={postSegment === "one-time"} onClick={() => setPostSegment("one-time")}>Однократные ({oneTime.length})</button></div>}
      {items === null ? <div className="skeleton list-skeleton" /> : visiblePosts.length === 0 ? <div className="empty-state"><strong>{emptyTitle}</strong><p>{postSegment === "recurring" ? "Настройте расписание или публикацию при изменении прайса." : "Создайте разовую публикацию на главной."}</p></div> : <div className="list-stack">{visiblePosts.map((post) => {
      const title = postListTitle(post);
      return <article className="list-card post-list-card" key={`${post.kind}:${post.id}`}><div className={`status-pin ${post.enabled ? "enabled" : ""}`} aria-hidden="true" /><div><div className="list-card-title"><strong title={title}>{title}</strong><span>{post.enabled ? "Активен" : "Выключен"}</span></div><p>{scheduleLabel(post)}</p>{post.deliveryState && <small>Доставка: {post.deliveryState}</small>}</div><div className="post-list-actions"><button type="button" aria-label={`Редактировать ${title}`} onClick={() => setSelected({ kind: post.kind, id: post.id })}>Редактировать</button><button className="danger-action" type="button" aria-label={`Удалить ${title}`} onClick={() => { setDeleteError(null); setDeleteCandidate({ post, title }); }}>Удалить</button></div></article>;
    })}</div>}
    </div>}
    {deleteCandidate && <div className="post-delete-backdrop"><section className="post-delete-confirm" role="dialog" aria-modal="true" aria-label="Удаление публикации"><h2>Удалить «{deleteCandidate.title}»?</h2><p>Расписание и все будущие публикации будут удалены.</p><p>Уже опубликованное сообщение в Telegram останется в группе.</p>{deleteError && <p className="preview-error" role="alert">{deleteError}</p>}<div><button type="button" disabled={deleting} onClick={() => setDeleteCandidate(null)}>Отмена</button><button className="danger-action" type="button" disabled={deleting} onClick={() => void confirmDelete()}>{deleting ? "Удаляем…" : "Удалить публикацию"}</button></div></section></div>}
  </section>;
}
